import { AlertTriangle, Check, Loader2, Upload } from 'lucide-react';
import type { CreativePreview } from '@/features/campaigns/types/campaign';
import { ContentPreviewMedia } from '@/features/campaigns/components/ContentPreviewMedia';
import type { AltCreativePreview } from '../PreviewStep.types';

type OrientationWarningBannerProps = {
  creativePreview: CreativePreview;
  bannerResolved: boolean;
  allUploadsReady: boolean;
  requiredLabel: string;
  warningTitle: string;
  warningBody: string;
  currentMediaOrientation: string;
  uploadTargets: readonly string[];
  altPreviewByOrientation: Record<string, AltCreativePreview>;
  uploadingOrientation: string | null;
  acceptedAiConvert: boolean;
  isLaunching?: boolean;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  onFileChange: (file?: File) => void;
  onOpenUpload: (orientation: string) => void;
  onDropUpload: (orientation: string, file?: File) => void;
  onRemoveAlt: (orientation: string) => void;
  onContinueWithAiConvert: () => void;
};

export function OrientationWarningBanner({
  creativePreview,
  bannerResolved,
  allUploadsReady,
  requiredLabel,
  warningTitle,
  warningBody,
  currentMediaOrientation,
  uploadTargets,
  altPreviewByOrientation,
  uploadingOrientation,
  acceptedAiConvert,
  isLaunching = false,
  fileInputRef,
  onFileChange,
  onOpenUpload,
  onDropUpload,
  onRemoveAlt,
  onContinueWithAiConvert,
}: OrientationWarningBannerProps) {
  return (
    <section
      className={`rounded-2xl border px-4 py-4 text-left sm:px-5 ${
        bannerResolved
          ? 'border-green-200 bg-green-50/70'
          : 'border-red-200 bg-red-50'
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept="image/*,video/*"
        onChange={(e) => void onFileChange(e.target.files?.[0])}
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
        <div className="flex shrink-0 items-center gap-3">
          <div className="h-16 w-16 overflow-hidden rounded-xl bg-[var(--dash-ink)] ring-1 ring-black/5">
            {creativePreview.url ? (
              <ContentPreviewMedia
                url={creativePreview.url}
                type={creativePreview.type}
                thumb
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-[10px] text-white/50">
                No content
              </div>
            )}
          </div>
          <div className="sm:hidden">
            <p className="m-0 text-xs font-semibold text-red-950">
              Your content
            </p>
            {currentMediaOrientation ? (
              <p className="m-0 mt-0.5 text-[11px] text-red-800/80">
                {currentMediaOrientation}
              </p>
            ) : null}
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            {bannerResolved ? (
              <Check
                className="mt-0.5 h-4 w-4 shrink-0 text-[var(--dash-success)]"
                strokeWidth={3}
              />
            ) : (
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
            )}
            <div className="min-w-0">
              <p
                className={`m-0 text-sm font-semibold ${
                  bannerResolved ? 'text-[var(--dash-ink)]' : 'text-red-900'
                }`}
              >
                {bannerResolved
                  ? allUploadsReady
                    ? 'Matching content added'
                    : 'Launching with AI convert'
                  : warningTitle || `Screens need ${requiredLabel}`}
              </p>
              {!bannerResolved ? (
                <p className="mt-1.5 mb-0 text-xs leading-relaxed text-red-800/90 sm:text-[13px]">
                  {warningBody}
                </p>
              ) : null}
              {!bannerResolved ? (
                <p className="mt-1 mb-0 hidden text-[11px] font-medium text-red-700/80 sm:block">
                  Current content
                  {currentMediaOrientation ? ` · ${currentMediaOrientation}` : ''}
                </p>
              ) : null}
            </div>
          </div>

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            {uploadTargets.map((orientation) => {
              const preview = altPreviewByOrientation[orientation];
              const busy = uploadingOrientation === orientation;

              if (busy && !preview) {
                return (
                  <div
                    key={orientation}
                    className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2.5 text-xs font-semibold text-blue-800"
                    aria-busy
                  >
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Uploading {orientation.toLowerCase()}…
                  </div>
                );
              }

              if (preview) {
                return (
                  <div
                    key={orientation}
                    className="relative flex max-w-sm items-center gap-2.5 overflow-hidden rounded-xl border border-green-200 bg-white px-2.5 py-2"
                  >
                    <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-[var(--dash-ink)]">
                      <ContentPreviewMedia
                        url={preview.url}
                        type={preview.type}
                        thumb
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="m-0 truncate text-xs font-semibold text-[var(--dash-ink)]">
                        {preview.name}
                      </p>
                      <p className="m-0 text-[10px] font-semibold tracking-wide text-[var(--dash-success)] uppercase">
                        {orientation}
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={isLaunching}
                      onClick={() => onRemoveAlt(orientation)}
                      className="shrink-0 text-[11px] font-semibold text-red-600 hover:underline disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </div>
                );
              }

              return (
                <button
                  key={orientation}
                  type="button"
                  disabled={Boolean(uploadingOrientation) || isLaunching}
                  onClick={() => onOpenUpload(orientation)}
                  onDragOver={(e) => {
                    if (!Array.from(e.dataTransfer.types).includes('Files'))
                      return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'copy';
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (uploadingOrientation || isLaunching) return;
                    onDropUpload(orientation, e.dataTransfer.files?.[0]);
                  }}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--dash-brand)] px-4 py-2.5 text-xs font-semibold text-white transition hover:opacity-95 disabled:opacity-50"
                >
                  <Upload className="h-3.5 w-3.5" />
                  Drop or upload {orientation.toLowerCase()}
                </button>
              );
            })}

            {!allUploadsReady ? (
              <button
                type="button"
                disabled={isLaunching}
                onClick={onContinueWithAiConvert}
                className={`inline-flex items-center gap-2 rounded-xl px-3.5 py-2.5 text-xs font-semibold transition disabled:opacity-60 ${
                  acceptedAiConvert || isLaunching
                    ? 'dash-btn-gradient text-white'
                    : 'border border-red-300 bg-white text-red-800 hover:bg-red-50'
                }`}
              >
                {isLaunching ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Launching…
                  </>
                ) : acceptedAiConvert ? (
                  <>
                    <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    Launching with AI convert…
                  </>
                ) : (
                  'Continue with AI convert'
                )}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}
